// HMR nudge: thumbnail + hover-play 2026-04-28
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  templateThumbnailUrl,
  useDeleteTemplateMutation,
  useUpdateTemplateMutation,
} from "@/api/templates";
import { useAuthStore } from "@/stores/auth";
import { applySvgFields } from "@/lib/svgFields";
import { CardHoverPlayer } from "./CardHoverPlayer";
import type { FieldDefinition, Template } from "@/types/template";

interface TemplateCardProps {
  template: Template;
}

/**
 * Build a data-URL rendering of an SVG template at its default field values.
 * Schema defaults are overlaid with the saved default_props so `{{key}}`
 * tokens resolve to the same look the author saved. Returns null for
 * non-SVG templates (they use the PNG thumbnail pipeline).
 */
function useSvgCardUrl(template: Template): string | null {
  return useMemo(() => {
    if (template.project_kind !== "svg") return null;
    const defaults: Record<string, unknown> = {};
    const schema: FieldDefinition[] = Array.isArray(template.field_schema)
      ? template.field_schema
      : [];
    for (const f of schema) defaults[f.key] = f.default;
    if (template.default_props && typeof template.default_props === "object") {
      Object.assign(defaults, template.default_props);
    }
    const svg = applySvgFields(template.tsx_code, defaults);
    // Standard percent-encoded form (NOT `;utf8,`): the browser decodes it
    // before parsing, so `#` in url(#id)/hex colors survives as %23 → #.
    return `data:image/svg+xml,${encodeURIComponent(svg)}`;
  }, [template]);
}

/**
 * Quick-action menu shown for owned templates only. See note on click
 * propagation in the parent component for why this lives outside the
 * <Link>.
 */
function TemplateCardMenu({ template }: { template: Template }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const updateMutation = useUpdateTemplateMutation(template.id);
  const deleteMutation = useDeleteTemplateMutation();

  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [open]);

  const stop = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <div ref={ref} className="absolute right-1.5 top-1.5 z-20">
      <button
        type="button"
        aria-label="Template actions"
        onClick={(e) => {
          stop(e);
          setOpen((v) => !v);
        }}
        className="flex h-7 w-7 items-center justify-center rounded-md border border-line bg-surface/90 text-sm text-ink-2 backdrop-blur transition-colors hover:text-ink"
      >
        ⋯
      </button>
      {open && (
        <div
          onClick={stop}
          className="absolute right-0 top-9 min-w-[140px] overflow-hidden rounded-lg border border-line bg-surface py-1 shadow-lg"
        >
          <button
            type="button"
            onClick={(e) => {
              stop(e);
              setOpen(false);
              const next = window.prompt("Rename template", template.name);
              if (next == null) return;
              const trimmed = next.trim();
              if (!trimmed || trimmed === template.name) return;
              updateMutation.mutate({ name: trimmed });
            }}
            className="block w-full px-3 py-1.5 text-left text-sm text-ink-2 transition-colors hover:bg-surface-3 hover:text-ink"
          >
            Rename
          </button>
          <button
            type="button"
            onClick={(e) => {
              stop(e);
              setOpen(false);
              const ok = window.confirm(
                `Delete "${template.name}"? This removes it from your dashboard. Existing renders are preserved.`,
              );
              if (!ok) return;
              deleteMutation.mutate(template.id);
            }}
            className="block w-full px-3 py-1.5 text-left text-sm text-danger transition-colors hover:bg-danger-soft"
          >
            Delete
          </button>
        </div>
      )}
    </div>
  );
}

export function TemplateCard({ template }: TemplateCardProps) {
  const currentUser = useAuthStore((s) => s.user);
  const isOwner =
    currentUser != null && template.user_id === currentUser.id;

  // Hover state drives both the lazy-mount of the Remotion Player and
  // its visual fade-in. Keeping the player unmounted at rest keeps the
  // dashboard cheap when there are dozens of cards.
  const [hovered, setHovered] = useState(false);
  // Whether the cached thumbnail PNG actually loaded. We optimistically
  // request it; if it 404s the <img> fires onError and we hide it,
  // falling back to the placeholder underneath.
  const [thumbOk, setThumbOk] = useState(true);
  const thumbUrl = templateThumbnailUrl(template);
  const svgUrl = useSvgCardUrl(template);
  const isSvg = svgUrl !== null;

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="group relative flex flex-col overflow-hidden rounded-xl border border-line bg-surface-2 transition-colors hover:border-line-strong"
    >
      <Link
        to="/templates/$id"
        params={{ id: template.id }}
        className="flex flex-col"
      >
        {/* Thumbnail / hover preview area */}
        <div className="relative aspect-video w-full overflow-hidden bg-surface-3">
          {isSvg ? (
            // SVG: render the substituted-defaults graphic directly (no PNG
            // thumbnail, no hover Player).
            <img
              src={svgUrl}
              alt={template.name}
              loading="lazy"
              className="absolute inset-0 h-full w-full object-contain p-3"
            />
          ) : (
            <>
              {/* Static thumbnail (default state). Hidden under the Player
                  when hovered. onError hides it so the placeholder shows. */}
              {thumbOk ? (
                <img
                  src={thumbUrl}
                  alt={template.name}
                  loading="lazy"
                  onError={() => setThumbOk(false)}
                  className="absolute inset-0 h-full w-full object-cover transition-transform group-hover:scale-105"
                />
              ) : (
                <div className="absolute inset-0 flex h-full items-center justify-center">
                  <span className="text-2xl font-semibold text-ink-3">MF</span>
                </div>
              )}

              {/* Hover-play overlay. Mounts only while hovered to avoid
                  compiling 12+ Remotion Players at once on dashboard load.
                  The Player fills the same box; on mouseleave we unmount,
                  which reveals the thumbnail underneath. */}
              {hovered && (
                <div className="absolute inset-0 h-full w-full">
                  <CardHoverPlayer template={template} />
                </div>
              )}
            </>
          )}

          {isSvg && (
            <div className="absolute left-2 top-2 z-10 rounded bg-surface/80 px-1.5 py-0.5 text-[10px] font-medium text-ink-2 backdrop-blur">
              SVG
            </div>
          )}

          <div className="absolute bottom-2 right-2 z-10 rounded bg-surface/80 px-2 py-0.5 text-xs text-ink-2 backdrop-blur">
            v{template.version}
          </div>
        </div>

        {/* Info */}
        <div className="flex flex-col gap-1 p-3">
          <h3 className="truncate text-sm font-semibold text-ink group-hover:text-accent">
            {template.name}
          </h3>
          <span className="text-xs text-ink-2">
            {template.category}
          </span>
          {template.description && (
            <p className="line-clamp-2 text-xs text-ink-3">
              {template.description}
            </p>
          )}
        </div>
      </Link>

      {isOwner && <TemplateCardMenu template={template} />}
    </div>
  );
}
